import { Fragment, useCallback, useEffect, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { EntityRef } from '../../components/EntityRef'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { ThemeToggle } from '../../theme/ThemeToggle'
import { useTheme } from '../../theme/ThemeProvider'
import { AccentPicker } from '../../theme/AccentPicker'
import { listConfigDefinitions, listProfiles, listUsers } from '../../api/admin'
import { listVersionSets } from '../../api/versionSets'
import { ApiError } from '../../api/problem'
import { humanize } from '../../lib/labels'
import type { ConfigDefinitionView, ProfileSummary, UserView } from '../../types/admin'
import type { VersionSetView } from '../../types/versionSet'

type AdminTab = 'settings' | 'preferences' | 'users' | 'versions'

const TABS: { key: AdminTab; label: string }[] = [
  { key: 'settings', label: 'Settings' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'users', label: 'Users & Roles' },
  { key: 'versions', label: 'Versions' },
]

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/**
 * `/admin/settings` — replaces the `ComingSoonStub`. Three read-only tabs over real platform/admin
 * data: the {@code ConfigDefinition} catalog (already-published `GET /config-definitions`), the
 * tenant's Users &amp; Profiles roster (two NEW thin additive GETs this story adds over the
 * already-published `UserApi`/`ProfileApi` ports), and a Versions tab (IB-167) reading the real
 * `GET /api/v1/version-sets` — the pinned VersionSet catalog ({@code version_set}, V1 baseline),
 * now with a repository/service/controller behind it, closing the gap a prior story disclosed here.
 */
export function AdminSettingsScreen() {
  const [tab, setTab] = useState<AdminTab>('settings')

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)' }}>
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="settings" size={22} />
        </span>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>Admin</h1>
          <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
            Platform configuration, the tenant's user roster, and effective-dated version sets.
          </p>
        </div>
      </div>

      <div role="tablist" aria-label="Admin sections" style={{ display: 'flex', gap: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className="iw-btn iw-btn--ghost"
            style={tab === t.key ? { borderBottom: '2px solid var(--iw-accent)', borderRadius: 0, color: 'var(--iw-accent)' } : { borderRadius: 0 }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' ? (
        <div role="tabpanel" aria-label="Settings">
          <WidgetErrorBoundary title="Config definitions" icon="settings">
            <ConfigDefinitionsPanel />
          </WidgetErrorBoundary>
        </div>
      ) : tab === 'preferences' ? (
        <div role="tabpanel" aria-label="Preferences">
          <PreferencesPanel />
        </div>
      ) : tab === 'users' ? (
        <div role="tabpanel" aria-label="Users & Roles" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
          <WidgetErrorBoundary title="Users" icon="users">
            <UsersPanel />
          </WidgetErrorBoundary>
          <WidgetErrorBoundary title="Profiles" icon="lock">
            <ProfilesPanel />
          </WidgetErrorBoundary>
        </div>
      ) : (
        <div role="tabpanel" aria-label="Versions">
          <WidgetErrorBoundary title="Version sets" icon="clock">
            <VersionsPanel />
          </WidgetErrorBoundary>
        </div>
      )}
    </div>
  )
}

function ConfigDefinitionsPanel() {
  const [rows, setRows] = useState<ConfigDefinitionView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    listConfigDefinitions()
      .then((page) => setRows(page.items))
      .catch((err) => setError(errorMessage(err, 'Could not load config definitions.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  if (error) {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }
  if (rows === null) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading config definitions…</p>
  }
  if (rows.length === 0) {
    return (
      <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
        <Icon name="settings" size={24} />
        <p>No config definitions are visible to this tenant yet.</p>
      </div>
    )
  }
  return (
    <table className="iw-table" aria-label="Config definitions">
      <thead>
        <tr>
          <th scope="col">Key</th>
          <th scope="col">Name</th>
          <th scope="col">Type</th>
          <th scope="col">Scopes</th>
          <th scope="col">Sensitive</th>
          <th scope="col">Origin</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id}>
            <td>
              <code>{c.key}</code>
            </td>
            <td>{c.name}</td>
            <td>{c.valueType}</td>
            <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
              {c.allowedScopes.join(', ')}
            </td>
            <td>
              {c.sensitive ? (
                <StatusPill variant="warning" label="Sensitive" icon="lock" />
              ) : (
                <span style={{ color: 'var(--iw-text-muted)' }}>—</span>
              )}
            </td>
            <td>
              <StatusPill variant={c.canon ? 'info' : 'neutral'} label={c.canon ? 'Platform canon' : 'Tenant extension'} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const THEME_PREFERENCE_DESCRIPTION: Record<'light' | 'dark' | 'system', string> = {
  light: 'Light is applied everywhere in this browser.',
  dark: 'Dark is applied everywhere in this browser.',
  system: "Follows this device's OS appearance setting, and updates automatically if that changes.",
}

/**
 * Roadmap #11 — the Settings-surface home for the theme preference (the
 * NavShell topbar carries a `compact` quick-toggle for the same preference;
 * both read/write the identical `ThemeProvider` state, so they can never
 * disagree). Currently `localStorage`-only — see `themePreference.ts`'s
 * `TODO: server-sync` — so this reads "this browser", not "this account",
 * until a real per-user preference endpoint exists.
 */
function PreferencesPanel() {
  const { preference } = useTheme()

  return (
    <div
      style={{
        background: 'var(--iw-surface)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-lg)',
        padding: 'var(--iw-space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-3)',
        maxWidth: 480,
      }}
    >
      <div>
        <h2 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-md)' }}>Theme</h2>
        <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
          Choose Light, Dark, or follow your device automatically.
        </p>
      </div>
      <ThemeToggle variant="full" />
      <p style={{ margin: 0, color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>
        {THEME_PREFERENCE_DESCRIPTION[preference]}
      </p>
      <div style={{ borderTop: '1px solid var(--iw-border)', paddingTop: 'var(--iw-space-3)' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-md)' }}>Accent colour</h2>
        <p style={{ margin: '0 0 var(--iw-space-2)', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>
          Pick one of 30 curated pastels, choose any custom colour, or reset to the default graphite.
        </p>
        <AccentPicker variant="full" />
      </div>
    </div>
  )
}

function UsersPanel() {
  const [rows, setRows] = useState<UserView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    listUsers()
      .then(setRows)
      .catch((err) => setError(errorMessage(err, 'Could not load users.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  return (
    <div
      style={{
        background: 'var(--iw-surface)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-lg)',
        padding: 'var(--iw-space-4)',
      }}
    >
      <h2 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Users</h2>
      {error ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : rows === null ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading users…</p>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
          <Icon name="users" size={24} />
          <p>No users in this tenant yet.</p>
        </div>
      ) : (
        <table className="iw-table" aria-label="Users">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Profile</th>
              <th scope="col">Status</th>
              <th scope="col">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.profileKey ?? '—'}</td>
                <td>
                  <StatusPill variant={u.status === 'active' ? 'success' : 'muted'} label={humanize(u.status)} />
                </td>
                <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                  {formatDateTime(u.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ProfilesPanel() {
  const [rows, setRows] = useState<ProfileSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    listProfiles()
      .then(setRows)
      .catch((err) => setError(errorMessage(err, 'Could not load profiles.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  return (
    <div
      style={{
        background: 'var(--iw-surface)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-lg)',
        padding: 'var(--iw-space-4)',
      }}
    >
      <h2 style={{ margin: '0 0 var(--iw-space-3)', fontSize: 'var(--iw-size-md)' }}>Profiles</h2>
      {error ? (
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
              Try again
            </button>
          }
        >
          {error}
        </InlineAlert>
      ) : rows === null ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading profiles…</p>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
          <Icon name="lock" size={24} />
          <p>No profiles are visible to this tenant yet.</p>
        </div>
      ) : (
        <table className="iw-table" aria-label="Profiles">
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Name</th>
              <th scope="col">Origin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td>
                  <code>{p.key}</code>
                </td>
                <td>{p.name}</td>
                <td>
                  <StatusPill variant={p.canon ? 'info' : 'neutral'} label={p.canon ? 'Platform canon' : 'Tenant extension'} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * `GET /api/v1/version-sets` (IB-167) — the tenant's pinned VersionSet catalog, newest first. Each
 * row expands to its pinned component versions (the {@code components} JSONB map, exposed as
 * {@code artifactKey -> pinnedVersion} pairs, sorted). If the demo tenant has never run a
 * finalization that pins a VersionSet, this renders an honest empty state rather than fabricating
 * rows — {@code version_set} has no public creation path in this codebase yet (a separate,
 * pre-existing gap disclosed in the module README).
 */
function VersionsPanel() {
  const [rows, setRows] = useState<VersionSetView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchRows = useCallback(() => {
    setError(null)
    setRows(null)
    setExpandedId(null)
    listVersionSets()
      .then((page) => setRows(page.items))
      .catch((err) => setError(errorMessage(err, 'Could not load version sets.')))
  }, [])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  if (error) {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={fetchRows}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }
  if (rows === null) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading version sets…</p>
  }
  if (rows.length === 0) {
    return (
      <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-5)' }}>
        <Icon name="clock" size={24} />
        <p>No version sets have been pinned for this tenant yet — finalization hasn&rsquo;t produced one.</p>
      </div>
    )
  }
  return (
    <table className="iw-table" aria-label="Version sets">
      <thead>
        <tr>
          <th scope="col">Label</th>
          <th scope="col">Pinned at</th>
          <th scope="col">Pinned by</th>
          <th scope="col">Pinned components</th>
          <th scope="col">Content hash</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((v) => {
          const expanded = expandedId === v.id
          return (
            <Fragment key={v.id}>
              <tr>
                <td>{v.label}</td>
                <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                  {formatDateTime(v.pinnedAt)}
                </td>
                <td>
                  <EntityRef kind="user" id={v.pinnedBy} />
                </td>
                <td>
                  <button
                    type="button"
                    className="iw-btn iw-btn--sm"
                    aria-expanded={expanded}
                    onClick={() => setExpandedId(expanded ? null : v.id)}
                  >
                    {v.components.length} {v.components.length === 1 ? 'artifact' : 'artifacts'}
                    <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={12} />
                  </button>
                </td>
                <td>
                  <code style={{ fontSize: 'var(--iw-size-xs)' }}>{v.contentHash}</code>
                </td>
              </tr>
              {expanded ? (
                <tr>
                  <td colSpan={5} style={{ background: 'var(--iw-surface-raised)' }}>
                    {v.components.length === 0 ? (
                      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>No pinned components recorded.</p>
                    ) : (
                      <table className="iw-table" aria-label={`Pinned components for ${v.label}`}>
                        <thead>
                          <tr>
                            <th scope="col">Artifact</th>
                            <th scope="col">Pinned version</th>
                          </tr>
                        </thead>
                        <tbody>
                          {v.components.map((c) => (
                            <tr key={c.artifactKey}>
                              <td>{humanize(c.artifactKey)}</td>
                              <td>
                                {typeof c.pinnedVersion === 'object' && c.pinnedVersion !== null
                                  ? JSON.stringify(c.pinnedVersion)
                                  : String(c.pinnedVersion)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              ) : null}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
